import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  TextField
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { EntityLabel, ReplicationRow, ReplicationRowValue } from '@esg/esg-global-types';
import { getReplicationRows } from '../../../../graphql/services/replication.service';

interface ReplicationRowSelectProps {
  handleChangeReplicationRows?: (
    replication_rows: Array<ReplicationRow> | ReplicationRow | null
  ) => void;
  options?: Array<ReplicationRow>;
  selected_options?: Array<ReplicationRow> | ReplicationRow | null;
  disabled?: boolean;
  input_label?: string;
  multi_select?: boolean;
  tag_limit?: number;
  group_id?: string;
  company_id?: string;
  replication_id?: string;
  table_id?: string;
  option_label_field?: string;
  replication_entity_label?: EntityLabel;
  loading?: boolean;
  auto_select?: boolean;
  clearable?: boolean;
  disable_selected_tags?: boolean;
  select_all?: boolean;
  variant?: 'standard' | 'outlined';
}

// Checkbox icons
const check_icon_blank: JSX.Element = <CheckBoxOutlineBlankIcon fontSize="small" />;
const check_icon_filled: JSX.Element = <CheckBoxIcon fontSize="small" />;

/**
 * ReplicationRow selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array< | ReplicationRow> |  | ReplicationRow | null} props.handleChangeReplicationRows Callback function to notify the parent component when the selected options have changed.
 * @param {Array< | ReplicationRow>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array< | ReplicationRow> |  | ReplicationRow | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {string} props.group_by Group the options by any one of a variety of grouping options. Defaults to ungrouped.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {string} props.group_id Query configured replication_rows for the provided group. Works in conjunction with props.company_id. Defaults to empty list.
 * @param {string} props.company_id Query configured replication_rows for the provided company. Works in conjunction with props.group_id. Defaults to empty list.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {boolean} props.auto_select Pre-populate the autocomplete input when only one replication_row is available.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @param {boolean} props.select_all Optionally add a select all checkbox to the input component of the autocomplete when multiple selection is allowed.
 * @param {string} props.variant An optional styling parameter for the textbox input of the autocomplete
 * @returns {JSX.Element}
 */
const ReplicationRowSelect = (props: ReplicationRowSelectProps) => {
  const [optionList, setOptions] = React.useState<Array<ReplicationRow>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<ReplicationRow> | ReplicationRow | null
  >(props.multi_select ? [] : null);

  const entity_label: EntityLabel = props.replication_entity_label ?? {
    one: 'Replication Row',
    many: 'Replication Rows'
  };

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<ReplicationRow> = props.options
        ? // ReplicationRows supplied by the parent component.
          props.options
        : props.group_id && props.company_id
          ? await getReplicationRows(
              {
                group_id: props.group_id,
                company_id: props.company_id,
                replication_id: props.replication_id ?? '',
                table_id: props.table_id ?? ''
              },
              {}
            )
          : [];
      if (options) setOptions(options);
      // When auto-select is true, automatically populate the autocomplete when only one standard is present.
      if (props.auto_select && options.length === 1 && options[0] !== selectedOptions) {
        setSelectedOptions(props.multi_select ? [options[0]] : options[0]);
        props.handleChangeReplicationRows &&
          props.handleChangeReplicationRows(props.multi_select ? [options[0]] : options[0]);
      }
    })();
  }, [props.group_id, props.company_id, props.options]);

  // Define selected options
  React.useEffect(() => {
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : props.selected_options
          ? props.selected_options
          : null
    );
  }, [props.options, props.selected_options]);

  const checkAll = (): boolean => {
    if (Array.isArray(selectedOptions)) {
      return optionList.length === selectedOptions.length;
    }
    return false;
  };

  const changeAll = (): void => {
    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
        props.handleChangeReplicationRows && props.handleChangeReplicationRows([]);
      } else {
        setSelectedOptions(optionList);
        props.handleChangeReplicationRows && props.handleChangeReplicationRows(optionList);
      }
    }
  };

  const getReplicationRowLabel = (replication_row: ReplicationRow): string => {
    if (!props.option_label_field) return replication_row.id;
    const option_label_value: ReplicationRowValue = replication_row?.data[props.option_label_field];
    return typeof option_label_value === 'string' ? option_label_value : replication_row.id;
  };

  return (
    <Box id="replication_row-select">
      <Autocomplete
        fullWidth
        disabled={props.disabled}
        loading={props.loading}
        limitTags={props.tag_limit}
        disableClearable={props.clearable === false}
        options={optionList}
        value={selectedOptions}
        getOptionLabel={(option: ReplicationRow) => getReplicationRowLabel(option)}
        disableCloseOnSelect={props.multi_select}
        isOptionEqualToValue={(option: ReplicationRow, value: ReplicationRow) =>
          option.id === value.id
        }
        multiple={props.multi_select}
        noOptionsText={`No ${entity_label.many.toLowerCase()}`}
        renderValue={
          !props.multi_select || !props.disable_selected_tags
            ? undefined
            : (value: ReplicationRow | Array<ReplicationRow>) => {
                return Array.isArray(value) && value.length > 0
                  ? `${value.length} ${value.length === 1 ? entity_label.one : entity_label.many}`
                  : undefined;
              }
        }
        renderOption={(render_props, option: ReplicationRow, { selected }) => (
          <Box
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...render_props}
            key={option.id}
          >
            {props.multi_select && (
              <Checkbox
                icon={check_icon_blank}
                checkedIcon={check_icon_filled}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {getReplicationRowLabel(option)}
          </Box>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={props.input_label ?? entity_label.one}
            variant={props.variant ?? 'standard'}
            slotProps={{
              htmlInput: { ...params.inputProps },
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {props.select_all && props.multi_select && (
                      <Box component="div">
                        <Box
                          component="li"
                          style={{
                            fontSize: '0.85rem',
                            color: '#616161',
                            fontWeight: 500
                          }}
                        >
                          <Checkbox
                            icon={check_icon_blank}
                            checkedIcon={check_icon_filled}
                            checked={checkAll()}
                            onChange={() => {
                              changeAll();
                            }}
                          />
                          Select All
                        </Box>
                      </Box>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
        onChange={(
          event: React.SyntheticEvent<Element, Event>,
          value: Array<ReplicationRow> | ReplicationRow | null
        ) => {
          setSelectedOptions(value);
          props.handleChangeReplicationRows && props.handleChangeReplicationRows(value);
        }}
      />
    </Box>
  );
};

export default ReplicationRowSelect;
